import { Bot, FileText, Inbox, PenSquare, Search } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useVenueScope } from "../../app/venueScope";
import { formatDateTime } from "../../lib/format";
import {
  Card,
  DataTable,
  EmptyState,
  ErrorPanel,
  Field,
  PageHeader,
  StatusPill,
} from "../../ui/components";
import { SkeletonTable } from "../../ui/skeletons";
import { threadPurposeLabels, threadStatusLabels, useEmailThreads } from "./queries";

// The mailbox orders by the last message rather than by when the exchange started. An
// exchange that has just moved comes back to the top, which is the only reason to look at
// a mailbox instead of the delivery log.
export function EmailsPage() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("open");
  const venue = useVenueScope();
  const threads = useEmailThreads(search, status, "", venue.selectedVenueId);

  return (
    <>
      <PageHeader
        title="Emails"
        description="Every conversation with your customers, in one place. Messages you send from here are recorded against the person they went to, so the next colleague to pick it up can see what was already said."
        actions={
          <div className="inline">
            <Link className="button secondary" to="/emails/mailboxes">
              <Inbox size={17} aria-hidden="true" /> Mailboxes
            </Link>
            <Link className="button secondary" to="/emails/templates">
              <FileText size={17} aria-hidden="true" /> Templates
            </Link>
            <Link className="button secondary" to="/emails/automation">
              <Bot size={17} aria-hidden="true" /> Automation
            </Link>
            <Link className="button primary" to="/emails/new">
              <PenSquare size={17} aria-hidden="true" /> New message
            </Link>
          </div>
        }
      />
      <Card>
        <div className="filters">
          <Field label="Search conversations">
            <span className="inline">
              <Search size={18} aria-hidden="true" />
              <input
                type="search"
                aria-label="Search conversations"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Subject, name, or organization"
              />
            </span>
          </Field>
          <Field label="Status">
            <select
              aria-label="Conversation status"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="open">Open</option>
              <option value="closed">Closed</option>
              <option value="">All conversations</option>
            </select>
          </Field>
        </div>
      </Card>
      <Card>
        {threads.error ? (
          <ErrorPanel error={threads.error} />
        ) : threads.isLoading ? (
          <SkeletonTable columns={6} rows={5} bare />
        ) : threads.data?.items.length ? (
          <DataTable
            caption="Email conversations"
            items={threads.data.items}
            getKey={(thread) => thread.id}
            defaultSort={{ label: "Last activity", direction: "descending" }}
            columns={[
              {
                label: "Conversation",
                sortValue: (thread) => thread.subject.toLowerCase(),
                render: (thread) => (
                  <Link className="button ghost small" to={`/emails/${thread.id}`}>
                    <strong>{thread.subject}</strong>
                    {thread.unreadCount > 0 ? (
                      // The number is the visible part and the word it means is added for a screen
                      // reader. This was an aria-label, but a bare <span> carries no role, and an
                      // aria-label on a role-less element is ignored by much of the assistive
                      // software it was written for. Real text in a visually hidden span is
                      // announced by all of it, and survives translation.
                      <span className="badge">
                        {thread.unreadCount}
                        <span className="sr-only"> unread</span>
                      </span>
                    ) : null}
                  </Link>
                ),
              },
              {
                // The customer is the recipient, so there is no separate column for who was
                // written to. There used to be one, reading "Several people" whenever a thread
                // was opened against an account rather than a named contact at it.
                label: "Customer",
                sortValue: (thread) => thread.customerName.toLowerCase(),
                render: (thread) => (
                  <Link className="button ghost small" to={`/contacts/${thread.customerId}`}>
                    {thread.customerName}
                  </Link>
                ),
              },
              {
                label: "Purpose",
                sortValue: (thread) => thread.purpose,
                render: (thread) => threadPurposeLabels[thread.purpose] ?? thread.purpose,
              },
              {
                label: "Messages",
                className: "numeric",
                sortValue: (thread) => thread.messageCount,
                render: (thread) => thread.messageCount,
              },
              {
                label: "Last activity",
                sortValue: (thread) => thread.lastMessageAt,
                render: (thread) => formatDateTime(thread.lastMessageAt),
              },
              {
                label: "Status",
                sortValue: (thread) => thread.status,
                render: (thread) => (
                  <StatusPill value={thread.status} label={threadStatusLabels[thread.status]} />
                ),
              },
            ]}
          />
        ) : (
          <EmptyState
            title="No conversations"
            description="Start one from here, or from the Email action on any contact."
            action={
              <Link className="button primary" to="/emails/new">
                <PenSquare size={17} aria-hidden="true" /> New message
              </Link>
            }
          />
        )}
      </Card>
    </>
  );
}
